import { Divider, IconButton, Stack, Typography } from '@mui/material';
import Iconify from 'src/components/iconify';
import {
  createTwoWayInterview,
  getTwoWayInterviewResult,
} from 'src/services/interview.service';
import { useAuthContext } from 'src/auth/hooks';
import { getOrganizationId } from 'src/services/user.service';
import { useSnackbar } from 'notistack';
import React, { useCallback, useEffect, useState } from 'react';
import { LoadingButton } from '@mui/lab';
import { useCopyToClipboard } from 'src/hooks/use-copy-to-clipboard';
import { INTERVIEW_CLIENT_URL } from 'src/config-global';
import { useCandidateProfileContext } from '../hooks/use-candidate-profile-context';
import 'react-responsive-carousel/lib/styles/carousel.min.css';
import { useSearchParams } from 'react-router-dom';
import Image from 'src/components/image';
import { makeStyles } from '@mui/styles';
import HorizontalBar from '../horizontal-bar-chart';
import { Carousel } from 'react-responsive-carousel';
import ReactPlayer from 'react-player';

const useStyles = makeStyles(theme => ({
  leftContainerAnalysis: {
    border: `1px solid ${theme.palette.border.primaryBlack3}`,
    padding: '18px 15px',
    gap: 34,
    width: '30%',
  },
  rightContainerAnalysis: {
    border: `1px solid ${theme.palette.border.primaryBlack3}`,
    padding: '18px 15px',
    paddingBottom: 0,
    width: '100%',
  },
  questionsDiv: {
    gap: 24,
  },
  questionItem: {
    border: `1px solid ${theme.palette.border.primaryBlack3}`,
    borderRadius: 6,
    padding: 20,
    gap: 16,
  },
  candidateCapturesDiv: {
    border: `1px solid ${theme.palette.border.primaryBlack3}`,
    borderRadius: 6,
    padding: 20,
    gap: 16,
  },
  transcript: {
    lineHeight: 'normal',
    color: theme.palette.content.primaryBlack2,
  },
  scoreBtn: {
    color: theme.palette.content.accent1,
    background: theme.palette.background.primaryAccent2,
    fontWeight: 600,
    '&:hover': {
      backgroundColor: theme.palette.background.primaryAccent2,
    },
  },
  disabledScoreBtn: {
    color: theme.palette.content.primaryBlack3,
    background: theme.palette.background.primaryGrey2,
    fontWeight: 600,
  },
  recordedBtn: {
    color: theme.palette.content.primaryBlack1,
    background: theme.palette.background.primaryGrey1,
    '&:hover': {
      backgroundColor: theme.palette.background.primaryGrey1,
    },
  },
  crossDiv: {
    background: theme.palette.background.primaryGrey3,
    padding: 4,
    borderRadius: 32,
    width: 30,
    height: 30,
    justifyContent: 'center',
    alignItems: 'center',
    position: 'relative',
    top: '4%',
    left: '92%',
    cursor: 'pointer',
    zIndex: 10,
  },
}));

export default function InteractiveInterview() {
  // hooks
  const {
    obj: candidateDetails,
    jobId,
    refetchWithoutLoading,
  } = useCandidateProfileContext();
  const { user } = useAuthContext();
  const orgId = getOrganizationId(user);
  const { copyToClipboard } = useCopyToClipboard();
  const { enqueueSnackbar } = useSnackbar();
  const [searchParams] = useSearchParams();
  const token = searchParams.get('token');
  const classes = useStyles();

  const isTwoWayInterviewCreated = candidateDetails?.two_way_interview;
  const interviewUrl = candidateDetails?.two_way_interview?.interview_url;
  const isInterviewCompleted =
    candidateDetails?.two_way_interview?.interview_status === 'COMPLETED';
  const candidateName = candidateDetails?.candidate_info?.name;

  // states
  const [isLoading, setIsLoading] = useState(false);
  const [isFetchingInterviewResult, setIsFetchingInterviewResult] =
    useState(false);
  const [skillAnalysis, setSkillAnalysis] = useState(null);
  const [interviewResult, setInterviewResult] = useState({});
  const [isInterviewLinkCopied, setIsInterviewLinkCopied] = useState(false);
  const [chatHistory, setChatHistory] = useState([]);

  // functions
  const createInterview = async () => {
    try {
      setIsLoading(true);

      const payload = {};

      const response = await createTwoWayInterview(
        orgId,
        jobId,
        candidateDetails?.candidate_info?.user_id,
        payload,
      );

      if (response?.success) {
        enqueueSnackbar('Created interactive interview');
      }
    } catch (err) {
      console.log('err: ', err);

      enqueueSnackbar('Failed to create interactive interview', {
        variant: 'error',
      });
    } finally {
      setIsLoading(false);
      refetchWithoutLoading();
    }
  };

  const handleCopyInterviewLink = () => {
    copyToClipboard({
      text: INTERVIEW_CLIENT_URL + interviewUrl,
      onSuccess: () => {
        setIsInterviewLinkCopied(true);
        setTimeout(() => {
          setIsInterviewLinkCopied(false);
        }, 2000);
      },
    });
  };

  const getButtonText = () => {
    if (isTwoWayInterviewCreated) {
      if (isInterviewLinkCopied) {
        return 'Copied!';
      }
      return 'Copy';
    }
    return 'Create';
  };

  const fetchInterviewResult = useCallback(async () => {
    try {
      setIsFetchingInterviewResult(true);

      const response = await getTwoWayInterviewResult(
        candidateDetails?.two_way_interview?.two_way_interview_id,
        token,
      );

      if (response?.success) {
        setInterviewResult(response?.data);
        const _chatHistory = response?.data?.session?.chat_history.reverse();

        const qaPairs = [];

        // Loop through the chat history array
        for (let i = 0; i < _chatHistory.length; i += 1) {
          // If the current message is from the assistant
          if (_chatHistory[i].role === 'assistant') {
            // Initialize a new pair with the question
            const pair = { question: _chatHistory[i].value, answer: null };

            // Check if the next message is from the user
            if (
              i + 1 < _chatHistory.length &&
              _chatHistory[i + 1].role === 'user'
            ) {
              // Add the user's answer to the pair
              pair.answer = _chatHistory[i + 1].value;
              // Skip the next message as it's already processed as the answer
              i += 1;
            }

            // Add the pair to the qaPairs array
            qaPairs.push(pair);
          }
        }

        // Set the formatted question-answer pairs to the state
        setChatHistory(qaPairs);
      } else throw new Error('Failed to fetch interview result');
    } catch (err) {
      console.log('err: ', err);

      enqueueSnackbar('Failed to fetch interview result', {
        variant: 'error',
      });
    } finally {
      setIsFetchingInterviewResult(false);
    }
  }, [
    candidateDetails?.two_way_interview?.two_way_interview_id,
    enqueueSnackbar,
    token,
  ]);

  // effects
  useEffect(() => {
    if (isInterviewCompleted) {
      fetchInterviewResult();
    }
  }, [isInterviewCompleted, fetchInterviewResult]);

  return isInterviewCompleted ? (
    <Stack spacing="30px" pb="18px">
      {/* <Stack direction="row" spacing="12px">
        <Stack className={classes.leftContainerAnalysis}>
          <Stack spacing="17px">
            <Stack spacing="10px" direction="row">
              <Typography variant="body1">Rank</Typography>
              <Image width="20px" src="/assets/circleI.svg" />
            </Stack>
            <Typography variant="h1">
              {candidateDetails?.interview_details?.rank || '_'}-
            </Typography>
          </Stack>
          <Divider />
          <Stack spacing="17px">
            <Typography variant="body1">Overall Score</Typography>
            <Typography variant="h1">
              {candidateDetails?.interview_details?.total_score === null
                ? '_'
                : candidateDetails?.interview_details?.total_score}
              -
            </Typography>
          </Stack>
        </Stack>
        <Stack className={classes.rightContainerAnalysis}>
          <Typography variant="subheading1">Analysis</Typography>
          {skillAnalysis ? (
            <HorizontalBar skillAnalysis={skillAnalysis} />
          ) : (
            <Stack
              sx={{ height: '25vh' }}
              spacing="12px"
              alignItems="center"
              justifyContent="center"
            >
              <Image width={48} height={48} src="/assets/no-data-found.svg" />
              <Typography variant="subheading2">No data</Typography>
            </Stack>
          )}
        </Stack>
      </Stack> */}
      {interviewResult?.video_recording_url ? (
        <Stack
          position="relative"
          pb="30px"
          sx={{ paddingX: '34px' }}
          bgcolor="#000000"
        >
          <ReactPlayer
            width="100%"
            height="400px"
            controls
            url={interviewResult?.video_recording_url}
          />
        </Stack>
      ) : (
        <Carousel showArrows showThumbs={false}>
          {interviewResult?.video_recordings?.map((recording, _index) => (
            <Stack
              position="relative"
              pb="30px"
              sx={{
                paddingX: '34px',
              }}
              bgcolor="#000000"
              key={_index}
            >
              <ReactPlayer
                width="100%"
                height="400px"
                controls
                url={recording}
              />
            </Stack>
          ))}
        </Carousel>
      )}
      {chatHistory.length > 0 ? (
        <Stack className={classes.questionsDiv}>
          {chatHistory.map((chat, index) => (
            <Stack key={index} className={classes.questionItem}>
              <Typography variant="subtitle1">AI : {chat?.question}</Typography>
              {index !== chatHistory.length - 1 && (
                <Typography variant="body1" className={classes.transcript}>
                  {candidateName || 'Candidate'} : {chat?.answer}
                </Typography>
              )}
            </Stack>
          ))}
        </Stack>
      ) : null}
    </Stack>
  ) : (
    <Stack
      height="100%"
      width="100%"
      alignItems="center"
      justifyContent="center"
    >
      <Stack alignItems="center" justifyContent="center" spacing="20px">
        <Iconify icon="tabler:mail-share" width={64} />
        <Typography fontSize={18}>
          {isTwoWayInterviewCreated ? 'Copy' : 'Create'} interactive assessment
          link
        </Typography>
        <LoadingButton
          loading={isLoading}
          variant="contained"
          startIcon={<Iconify icon="tabler:code" />}
          sx={{
            width: '220px',
            bgcolor: isInterviewLinkCopied
              ? 'background.primaryGreen2'
              : 'background.primaryBlack1',
            color: isInterviewLinkCopied
              ? 'content.primaryGreen1'
              : 'content.primaryWhite',

            '&:hover': {
              color: isInterviewLinkCopied
                ? 'background.primaryGreen2'
                : 'content.primaryWhite',
              bgcolor: isInterviewLinkCopied
                ? 'content.primaryGreen1'
                : 'background.primaryAccent1',
            },
          }}
          onClick={() => {
            if (!isTwoWayInterviewCreated) {
              createInterview();
            } else if (interviewUrl) {
              handleCopyInterviewLink();
            }
          }}
        >
          {getButtonText()}
        </LoadingButton>
      </Stack>
    </Stack>
  );
}
